import { ReactNode, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Check, X, ExternalLink } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import FilesTab from '../components/FilesTab'
import { Dash } from '@/components/ui/inline-edit'

type TabKey = 'overview' | 'files'

// ─────────────────── Types ───────────────────
type JournalLine = {
  id: number
  account: number
  account_code: string
  account_name: string
  description: string
  partner: number | null
  partner_name: string | null
  debit: string
  credit: string
  sort_order: number
}

type JournalEntry = {
  id: number
  number: string
  date: string
  description: string
  source_type: 'INVOICE' | 'PAYMENT' | 'BILL' | 'MANUAL'
  source_id: number | null
  currency_code: string
  total_debit: string
  total_credit: string
  is_balanced: boolean
  lines: JournalLine[]
}

// ─────────────────── Constants ───────────────────
const SOURCE_BADGE: Record<JournalEntry['source_type'], string> = {
  INVOICE: 'bg-blue-100 text-blue-800',
  PAYMENT: 'bg-green-100 text-green-800',
  BILL: 'bg-amber-100 text-amber-800',
  MANUAL: 'bg-gray-100 text-gray-700',
}

const SOURCE_LABEL: Record<JournalEntry['source_type'], string> = {
  INVOICE: 'Invoice',
  PAYMENT: 'Payment',
  BILL: 'Bill',
  MANUAL: 'Manual entry',
}

const SOURCE_RECORD_PATH: Record<JournalEntry['source_type'], string | null> = {
  INVOICE: '/admin/finance/invoices',
  PAYMENT: '/admin/finance/payments',
  BILL: '/admin/finance/bills',
  MANUAL: null,
}

// ─────────────────── Page ───────────────────
export default function FinanceJournalEntryDetail() {
  const { id } = useParams<{ id: string }>()
  const entryId = id ? Number(id) : 0

  const entryQ = useQuery({
    queryKey: ['finance', 'journal-entry', entryId],
    queryFn: async () => (await api.get<JournalEntry>(`/finance/journal-entries/${entryId}/`)).data,
    enabled: !!entryId,
  })

  const [tab, setTab] = useState<TabKey>('overview')

  if (entryQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (entryQ.isError || !entryQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Journal entry not found.</p>
          <Link to="/admin/finance/journal-entries" className="text-blue-600 hover:underline">← Back to journal entries</Link>
        </div>
      </AdminLayout>
    )
  }

  const je = entryQ.data
  const sourcePath = SOURCE_RECORD_PATH[je.source_type]
  const sourceLink = sourcePath && je.source_id ? `${sourcePath}/${je.source_id}` : null
  const lines = [...je.lines].sort((a, b) => a.sort_order - b.sort_order)

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/finance/journal-entries" className="text-muted-foreground hover:text-foreground hover:underline">← Journal entries</Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-xs text-muted-foreground">{je.date}</div>
            <h2 className="text-2xl font-bold text-foreground font-mono">{je.number}</h2>
            <div className="text-sm text-muted-foreground flex items-center gap-2 flex-wrap">
              <span className={`text-xs px-2 py-0.5 rounded font-semibold ${SOURCE_BADGE[je.source_type]}`}>
                {SOURCE_LABEL[je.source_type]}
              </span>
              {je.description && <span>· {je.description}</span>}
              <span>·</span>
              <span className="font-mono">{je.currency_code}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {je.is_balanced ? (
              <span className="text-xs px-2 py-0.5 rounded font-semibold bg-green-100 text-green-800 inline-flex items-center gap-1">
                <Check className="h-3 w-3" /> BALANCED
              </span>
            ) : (
              <span className="text-xs px-2 py-0.5 rounded font-semibold bg-red-100 text-red-700 inline-flex items-center gap-1">
                <X className="h-3 w-3" /> OUT OF BALANCE
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['overview', 'files'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'overview' && 'Overview'}
              {t === 'files' && 'Files'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'overview' && (
      <>
      {/* Read-only banner — JEs are system-managed */}
      <div className="bg-muted/40 border rounded p-3 text-sm text-muted-foreground mb-6">
        Journal entries are created automatically when invoices, bills, or payments are posted to the General Ledger. They are read-only here — to reverse this entry, void or reverse the source document.
      </div>

      <div className="space-y-6 max-w-4xl">
        {/* Source document link */}
        {sourceLink && (
          <Section title="Source document">
            <Link to={sourceLink} className="inline-flex items-center gap-2 text-blue-600 hover:underline">
              <ExternalLink className="h-4 w-4" />
              Open {SOURCE_LABEL[je.source_type].toLowerCase()} record
            </Link>
          </Section>
        )}

        {/* Lines */}
        <section className="bg-card rounded-lg shadow overflow-hidden">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-6 py-3 border-b">
            Lines ({lines.length})
          </h3>
          {lines.length === 0 ? (
            <p className="text-muted-foreground text-sm p-6">No lines on this entry.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left">
                <tr>
                  <Th>Account</Th>
                  <Th>Description</Th>
                  <Th>Partner</Th>
                  <Th className="text-right">Debit</Th>
                  <Th className="text-right">Credit</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {lines.map((l) => (
                  <tr key={l.id}>
                    <Td>
                      <span className="font-mono text-xs">{l.account_code}</span>{' '}
                      <span className="text-muted-foreground">{l.account_name}</span>
                    </Td>
                    <Td className="text-xs">{l.description || <Dash />}</Td>
                    <Td className="text-xs">{l.partner_name || <Dash />}</Td>
                    <Td className="text-right font-mono">
                      {Number(l.debit) > 0
                        ? Number(l.debit).toFixed(2)
                        : <span className="text-muted-foreground">—</span>}
                    </Td>
                    <Td className="text-right font-mono">
                      {Number(l.credit) > 0
                        ? Number(l.credit).toFixed(2)
                        : <span className="text-muted-foreground">—</span>}
                    </Td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-muted/40">
                <tr className="font-semibold">
                  <td colSpan={3} className="px-4 py-2 text-right text-muted-foreground uppercase text-xs">Totals</td>
                  <td className="px-4 py-2 text-right font-mono">
                    {je.currency_code} {Number(je.total_debit).toFixed(2)}
                  </td>
                  <td className="px-4 py-2 text-right font-mono">
                    {je.currency_code} {Number(je.total_credit).toFixed(2)}
                  </td>
                </tr>
                {!je.is_balanced && (
                  <tr>
                    <td colSpan={5} className="px-4 py-2 text-right text-red-700 text-xs">
                      Difference: {(Number(je.total_debit) - Number(je.total_credit)).toFixed(2)} {je.currency_code}
                    </td>
                  </tr>
                )}
              </tfoot>
            </table>
          )}
        </section>
      </div>
      </>
      )}
      {tab === 'files' && <FilesTab parent="finance.journalentry" parentId={entryId} />}
    </AdminLayout>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-card rounded-lg shadow p-6">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{title}</h3>
      {children}
    </section>
  )
}

// ─────────────────── Helpers ───────────────────
function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase ${className}`}>{children}</th>
}
function Td({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <td className={`px-4 py-2 text-foreground ${className}`}>{children}</td>
}
